/**
 * SVG path (`d`) utilities for export — brief 01-export.md Mode 3.
 *
 * Trace-layer `d` strings are always built by geometry/trace.ts's `buildTraceD`,
 * so the command set is small and fixed: `M x y`, `C x1 y1 x2 y2 x y`, `Z`. These
 * helpers only need to support that subset — not a general SVG path grammar.
 */
import { apply, type Mat } from '@/geometry/affine';

export type PathCommand =
  | { cmd: 'M' | 'L'; x: number; y: number }
  | { cmd: 'C'; x1: number; y1: number; x2: number; y2: number; x: number; y: number }
  | { cmd: 'Z' };

/** Parse a `buildTraceD`-shaped path string into commands. */
export function parsePathD(d: string): PathCommand[] {
  const tokens = d.trim().split(/\s+/).filter(Boolean);
  const out: PathCommand[] = [];
  let i = 0;
  const num = (): number => Number(tokens[i++]);
  while (i < tokens.length) {
    const cmd = tokens[i++];
    if (cmd === 'M' || cmd === 'L') {
      out.push({ cmd, x: num(), y: num() });
    } else if (cmd === 'C') {
      out.push({ cmd: 'C', x1: num(), y1: num(), x2: num(), y2: num(), x: num(), y: num() });
    } else if (cmd === 'Z') {
      out.push({ cmd: 'Z' });
    } else {
      // Unknown token — shouldn't happen for buildTraceD output, but don't loop forever.
      break;
    }
  }
  return out;
}

function fmt(n: number): string {
  return Number(n.toFixed(4)).toString();
}

export function serializePathD(commands: readonly PathCommand[]): string {
  return commands
    .map((c) => {
      if (c.cmd === 'Z') return 'Z';
      if (c.cmd === 'C') return `C ${fmt(c.x1)} ${fmt(c.y1)} ${fmt(c.x2)} ${fmt(c.y2)} ${fmt(c.x)} ${fmt(c.y)}`;
      return `${c.cmd} ${fmt(c.x)} ${fmt(c.y)}`;
    })
    .join(' ');
}

/**
 * Resolve an affine transform into absolute path coordinates (Mode 3's Cricut
 * requirement: no `transform` attribute, coordinates baked into `d` itself).
 */
export function transformPathD(d: string, m: Mat): string {
  const commands = parsePathD(d).map((c): PathCommand => {
    if (c.cmd === 'Z') return c;
    if (c.cmd === 'C') {
      const [x1, y1] = apply(m, [c.x1, c.y1]);
      const [x2, y2] = apply(m, [c.x2, c.y2]);
      const [x, y] = apply(m, [c.x, c.y]);
      return { cmd: 'C', x1, y1, x2, y2, x, y };
    }
    const [x, y] = apply(m, [c.x, c.y]);
    return { cmd: c.cmd, x, y };
  });
  return serializePathD(commands);
}

/** Sample a cubic Bézier at `segments` even parameter steps (excluding t=0). */
function sampleCubic(
  p0: [number, number],
  p1: [number, number],
  p2: [number, number],
  p3: [number, number],
  segments: number,
): [number, number][] {
  const pts: [number, number][] = [];
  for (let s = 1; s <= segments; s++) {
    const t = s / segments;
    const mt = 1 - t;
    const a = mt * mt * mt;
    const b = 3 * mt * mt * t;
    const c = 3 * mt * t * t;
    const e = t * t * t;
    pts.push([
      a * p0[0] + b * p1[0] + c * p2[0] + e * p3[0],
      a * p0[1] + b * p1[1] + c * p2[1] + e * p3[1],
    ]);
  }
  return pts;
}

/**
 * Flatten a path into one or more polylines of absolute points (for DXF, which
 * has no native cubic-Bézier path entity Cricut/Silhouette read reliably —
 * LWPOLYLINE is the safe, universally-supported choice).
 */
export function flattenPathToPolylines(d: string, segmentsPerCurve = 24): [number, number][][] {
  const commands = parsePathD(d);
  const polylines: [number, number][][] = [];
  let current: [number, number][] = [];
  let cursor: [number, number] = [0, 0];
  let start: [number, number] = [0, 0];
  for (const c of commands) {
    if (c.cmd === 'M') {
      if (current.length > 0) polylines.push(current);
      current = [[c.x, c.y]];
      cursor = [c.x, c.y];
      start = cursor;
    } else if (c.cmd === 'L') {
      current.push([c.x, c.y]);
      cursor = [c.x, c.y];
    } else if (c.cmd === 'C') {
      current.push(...sampleCubic(cursor, [c.x1, c.y1], [c.x2, c.y2], [c.x, c.y], segmentsPerCurve));
      cursor = [c.x, c.y];
    } else if (c.cmd === 'Z') {
      current.push(start);
      cursor = start;
    }
  }
  if (current.length > 0) polylines.push(current);
  return polylines;
}

/** Bounding box of a path's flattened points — good enough for viewBox/DXF extents. */
export function pathBounds(d: string): { minX: number; minY: number; maxX: number; maxY: number } | null {
  const pts = flattenPathToPolylines(d).flat();
  if (pts.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of pts) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}
